import React, { useState, useEffect, useContext } from 'react';
import { Truck, Plus, Edit3, Trash2, Search, Filter, Eye, Wrench, MessageSquare, History, FileText, AlertTriangle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { UserContext } from '../../contexts/UserContext';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const VehicleManagement = () => {
  const { user } = useContext(UserContext);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReceiveItemModal, setShowReceiveItemModal] = useState(false);
  const [selectedIssueItem, setSelectedIssueItem] = useState(null);
  const [equipmentTypes, setEquipmentTypes] = useState([]);

  useEffect(() => {
    fetchVehicles();
    fetchEquipmentTypes();
  }, []);

  const fetchEquipmentTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/car-equipment-types/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      setEquipmentTypes(response.data);
    } catch (error) {
      console.error('Error fetching equipment types:', error);
      // Fallback to default types if API fails
      setEquipmentTypes([
        { id: 1, code: 'car', name: 'Car' },
        { id: 2, code: 'machine', name: 'Machine' },
        { id: 3, code: 'lighting_tower', name: 'Lighting Tower' },
        { id: 4, code: 'cherry_picker', name: 'Cherry Picker' },
        { id: 5, code: 'lift', name: 'Lift' }
      ]);
    }
  };

  const handleReturnVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowReturnModal(true);
  };

  const handleReceiveItem = (item) => {
    setSelectedIssueItem(item);
    setShowReceiveItemModal(true);
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/cars/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      let vehiclesData = response.data.results || response.data || [];

      // Fetch current assignments and urgent issues for each vehicle
      const vehiclesWithData = await Promise.all(vehiclesData.map(async (vehicle) => {
        let vehicleWithData = { ...vehicle };
        
        // Fetch assignments if issued
        if (vehicle.car_status === 'issued') {
          try {
            const assignmentResponse = await axios.get(`${API_BASE_URL}/api/vehicle-assignments/?car=${vehicle.id}&status=assigned`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
            });
            if (assignmentResponse.data.results && assignmentResponse.data.results.length > 0) {
              vehicleWithData.current_assignment = assignmentResponse.data.results[0];
            }
          } catch (error) {
            console.error(`Error fetching assignment for vehicle ${vehicle.id}:`, error);
          }
        }
        
        // Fetch urgent issues count (only unresolved)
        try {
          const commentsResponse = await axios.get(`${API_BASE_URL}/api/vehicle-comments/?car=${vehicle.id}&is_urgent=true`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          });
          const allUrgentComments = commentsResponse.data.results || commentsResponse.data || [];
          // Filter out resolved issues on frontend side
          const unresolvedUrgentComments = allUrgentComments.filter(comment => 
            !comment.is_resolved && comment.is_resolved !== true
          );
          vehicleWithData.urgent_issues_count = unresolvedUrgentComments.length;
          console.log(`Vehicle ${vehicle.id}: ${allUrgentComments.length} total urgent, ${unresolvedUrgentComments.length} unresolved`);
          console.log('Sample urgent comment:', allUrgentComments[0]); // Debug first comment structure
        } catch (error) {
          console.error(`Error fetching urgent issues for vehicle ${vehicle.id}:`, error);
          vehicleWithData.urgent_issues_count = 0;
        }
        
        return vehicleWithData;
      }));

      setVehicles(Array.isArray(vehiclesWithData) ? vehiclesWithData : []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = () => {
    setEditingVehicle(null);
    setShowVehicleModal(true);
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowVehicleModal(true);
  };

  const handleVehicleDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetailsModal(true);
  };

  const handleQuickRepair = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowRepairModal(true);
  };

  const handleAddComment = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowCommentModal(true);
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/cars/${vehicleId}/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      fetchVehicles();
      alert('Vehicle deleted successfully');
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert('Error deleting vehicle');
    }
  };

  const handleSaveVehicle = async (vehicleData) => {
    try {
      if (editingVehicle) {
        // Check if status is being changed to 'available' - this requires special handling
        const oldStatus = editingVehicle.car_status;
        const newStatus = vehicleData.car_status;
        
        if (oldStatus !== 'available' && newStatus === 'available') {
          // Use the special status change endpoint that automatically clears items
          await axios.post(`${API_BASE_URL}/api/vehicles/${editingVehicle.id}/change-status/`, {
            status: 'available',
            location: 'Warehouse',
            notes: 'Vehicle returned to warehouse and made available'
          }, {
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          // Update other vehicle properties (excluding status which was already updated)
          const { car_status, ...otherData } = vehicleData;
          if (Object.keys(otherData).length > 0) {
            await axios.put(`${API_BASE_URL}/api/cars/${editingVehicle.id}/`, otherData, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
            });
          }
          
          alert('Vehicle updated successfully! All assigned items were automatically returned to warehouse.');
        } else {
          // Regular update for other status changes
          await axios.put(`${API_BASE_URL}/api/cars/${editingVehicle.id}/`, vehicleData, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          });
          alert('Vehicle updated successfully');
        }
      } else {
        await axios.post(`${API_BASE_URL}/api/cars/`, vehicleData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        alert('Vehicle created successfully');
      }
      
      fetchVehicles();
      setShowVehicleModal(false);
      setEditingVehicle(null);
    } catch (error) {
      console.error('Error saving vehicle:', error);
      if (error.response?.status === 403) {
        alert('Permission denied. Please check your access rights.');
      } else {
        alert('Error saving vehicle');
      }
    }
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = searchTerm === '' ||
      vehicle.car_brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.car_model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.plate_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.side_number?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (statusFilter === 'urgent_issues') {
      matchesStatus = vehicle.urgent_issues_count > 0;
    } else if (statusFilter !== 'all') {
      matchesStatus = vehicle.car_status === statusFilter;
    }

    const matchesType = typeFilter === 'all' || vehicle.equipment_type === typeFilter;

    const matchesCondition = conditionFilter === 'all' || vehicle.technical_condition === conditionFilter;

    return matchesSearch && matchesStatus && matchesType && matchesCondition;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-steel-50 p-6">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                Equipment Management
              </h1>
              <p className="text-slate-600">Manage your equipment - cars, machines, lifts, and other tools</p>
            </div>
            <button
              onClick={handleAddVehicle}
              className="bg-brand-600 text-white px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20 flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Equipment
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative lg:col-span-1">
              <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            {/* Equipment Type Filter */}
            <div className="relative">
              <Truck className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                {equipmentTypes.map(type => (
                  <option key={type.id} value={type.code}>{type.name}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="urgent_issues">🚨 Urgent Issues</option>
                <option value="available">Available</option>
                <option value="service">Service</option>
                <option value="reservation">Reservation</option>
                <option value="issued">Issued</option>
                <option value="damaged">Damaged</option>
              </select>
            </div>

            {/* Condition Filter */}
            <div className="relative">
              <Wrench className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <select
                value={conditionFilter}
                onChange={(e) => setConditionFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="all">All Conditions</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>

            {/* Results Count & Clear */}
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-slate-600">
                {filteredVehicles.length} of {vehicles.length}
              </span>
              {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || conditionFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setTypeFilter('all');
                    setConditionFilter('all');
                  }}
                  className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Vehicles Table */}
        {filteredVehicles.length === 0 ? (
          <div className="text-center py-12">
            <Truck className="h-16 w-16 text-steel-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No vehicles found</h3>
            <p className="text-slate-500 mb-4">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first vehicle.'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={handleAddVehicle}
                className="bg-brand-600 text-white px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20"
              >
                Add First Vehicle
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-brand-200">
            <table className="min-w-full divide-y divide-steel-200">
                  <thead className="bg-steel-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Vehicle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Plate Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Fleet Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Current Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Condition
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Issues
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Seats
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        NFC Tag
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Mileage
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-steel-200">
                    {filteredVehicles.map(vehicle => (
                      <tr key={vehicle.id} className="hover:bg-steel-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-slate-900">
                            {vehicle.car_brand} {vehicle.car_model}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{vehicle.plate_number}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{vehicle.side_number || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">
                            {vehicle.current_assignment 
                              ? `${vehicle.current_assignment.project_name || 'On Assignment'}` 
                              : (vehicle.current_location || 'Warehouse')
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${
                            vehicle.technical_condition === 'excellent' ? 'text-green-600' :
                            vehicle.technical_condition === 'good' ? 'text-brand-600' :
                            vehicle.technical_condition === 'fair' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {vehicle.technical_condition || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {vehicle.urgent_issues_count > 0 ? (
                            <div 
                              className="flex items-center cursor-pointer hover:bg-red-50 p-1 rounded"
                              title={`Vehicle has ${vehicle.urgent_issues_count} urgent issue${vehicle.urgent_issues_count > 1 ? 's' : ''}. Click Details to view.`}
                              onClick={() => handleVehicleDetails(vehicle)}
                            >
                              <AlertTriangle className="w-4 h-4 text-red-500 mr-1 animate-pulse" />
                              <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">
                                {vehicle.urgent_issues_count} URGENT
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{vehicle.number_of_seats || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {vehicle.nfc_tag_number ? (
                            <span className="font-mono text-xs bg-steel-100 px-2 py-1 rounded">
                              {vehicle.nfc_tag_number}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{vehicle.car_mileage || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              onClick={() => handleVehicleDetails(vehicle)}
                              className="text-indigo-600 hover:text-indigo-800 p-1"
                              title="Vehicle Details & History"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditVehicle(vehicle)}
                              className="text-brand-600 hover:text-brand-800 p-1"
                              title="Edit Vehicle"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleQuickRepair(vehicle)}
                              className="text-orange-600 hover:text-orange-800 p-1"
                              title="Quick Repair"
                            >
                              <Wrench className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleAddComment(vehicle)}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Add Comment"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteVehicle(vehicle.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete Vehicle"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {/* Vehicle Modal */}
      {showVehicleModal && (
        <VehicleModal
          vehicle={editingVehicle}
          onClose={() => {
            setShowVehicleModal(false);
            setEditingVehicle(null);
          }}
          onSave={handleSaveVehicle}
          equipmentTypes={equipmentTypes}
        />
      )}

      {/* Vehicle Details Modal */}
      {showDetailsModal && (
        <VehicleDetailsModal
          vehicle={selectedVehicle}
          user={user}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedVehicle(null);
          }}
          onReceiveItem={handleReceiveItem}
          onRefreshVehicles={fetchVehicles}
        />
      )}

      {/* Quick Repair Modal */}
      {showRepairModal && (
        <QuickRepairModal
          vehicle={selectedVehicle}
          onClose={() => {
            setShowRepairModal(false);
            setSelectedVehicle(null);
          }}
          onSave={async (repairData) => {
            try {
              // Save repair record
              await axios.post(`${API_BASE_URL}/api/vehicle-repairs/`, repairData, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                  'Content-Type': 'application/json'
                }
              });

              // Update vehicle mileage and status if repair includes these updates
              if (repairData.mileage_at_repair || repairData.update_vehicle_status) {
                const vehicleUpdateData = {};
                
                if (repairData.mileage_at_repair) {
                  vehicleUpdateData.car_mileage = repairData.mileage_at_repair;
                }
                
                // If repair is urgent or affects availability, update car status
                if (repairData.priority === 'urgent' || repairData.priority === 'high') {
                  vehicleUpdateData.car_status = 'service';
                }

                if (Object.keys(vehicleUpdateData).length > 0) {
                  await axios.patch(`${API_BASE_URL}/api/cars/${selectedVehicle.id}/`, vehicleUpdateData, {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                      'Content-Type': 'application/json'
                    }
                  });
                }
              }

              setShowRepairModal(false);
              setSelectedVehicle(null);
              fetchVehicles(); // Refresh vehicle list
              alert('Repair recorded successfully! Vehicle status and mileage updated.');
            } catch (error) {
              console.error('Error saving repair:', error);
              alert('Error saving repair. Please try again.');
            }
          }}
        />
      )}

      {/* Add Comment Modal */}
      {showCommentModal && (
        <AddCommentModal
          vehicle={selectedVehicle}
          user={user}
          onClose={() => {
            setShowCommentModal(false);
            setSelectedVehicle(null);
          }}
          onSave={async (commentData) => {
            try {
              console.log('Sending comment data:', commentData);
              console.log('Selected vehicle:', selectedVehicle);
              console.log('User from localStorage:', JSON.parse(localStorage.getItem('user') || '{}'));
              
              // Save comment
              const response = await axios.post(`${API_BASE_URL}/api/vehicle-comments/`, commentData, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                  'Content-Type': 'application/json'
                }
              });
              console.log('Comment response:', response.data);

              // Update vehicle mileage if provided
              if (commentData.mileage_at_comment && commentData.mileage_at_comment !== selectedVehicle.car_mileage) {
                await axios.patch(`${API_BASE_URL}/api/cars/${selectedVehicle.id}/`, {
                  car_mileage: commentData.mileage_at_comment
                }, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json'
                  }
                });
              }

              setShowCommentModal(false);
              setSelectedVehicle(null);
              fetchVehicles(); // Refresh vehicle list
              alert('Comment added successfully! Vehicle mileage updated.');
            } catch (error) {
              console.error('Error saving comment:', error);
              console.error('Error response:', error.response?.data);
              console.error('Error status:', error.response?.status);
              alert(`Error saving comment: ${error.response?.data?.detail || error.response?.data?.message || error.message}`);
            }
          }}
        />
      )}
    </div>
  );
};

const VehicleModal = ({ vehicle, onClose, onSave, equipmentTypes = [] }) => {
  const [formData, setFormData] = useState({
    equipment_type: 'car', // Type of equipment: car, machine, lighting_tower, cherry_picker, lift
    car_brand: '',
    car_model: '',
    plate_number: '',
    side_number: '',
    number_of_seats: '',
    nfc_tag_number: '',
    car_status: 'available',
    technical_condition: 'good',
    car_mileage: '',
    comment: '',
    // Rental fields
    ownership_type: 'own',
    rental_start_date: '',
    rental_end_date: '',
    daily_rental_cost: ''
  });

  // States for equipment type management
  const [localEquipmentTypes, setLocalEquipmentTypes] = useState(equipmentTypes);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeCode, setNewTypeCode] = useState('');
  const [typeError, setTypeError] = useState('');

  React.useEffect(() => {
    setLocalEquipmentTypes(equipmentTypes);
  }, [equipmentTypes]);

  // Fetch types from API
  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/car-equipment-types/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      setLocalEquipmentTypes(response.data);
    } catch (error) {
      console.error('Error fetching equipment types:', error);
    }
  };

  // Add new equipment type
  const handleAddType = async (e) => {
    e.preventDefault();
    setTypeError('');

    try {
      await axios.post(
        `${API_BASE_URL}/api/car-equipment-types/`,
        { name: newTypeName, code: newTypeCode },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
      );

      // Refresh types
      await fetchTypes();

      // Reset form
      setNewTypeName('');
      setNewTypeCode('');
      setShowTypeForm(false);

      // Auto-select new type
      setFormData(prev => ({ ...prev, equipment_type: newTypeCode }));
    } catch (error) {
      console.error('Error adding equipment type:', error);
      setTypeError(error.response?.data?.code?.[0] || 'Failed to add type');
    }
  };

  // Delete equipment type
  const handleDeleteType = async (typeId, typeCode) => {
    try {
      // Check usage
      const usageResponse = await axios.get(
        `${API_BASE_URL}/api/car-equipment-types/${typeId}/check-usage/`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
      );

      if (usageResponse.data.is_used) {
        if (!window.confirm(`This type is used by ${usageResponse.data.cars_count} vehicle(s). Deactivate it?`)) {
          return;
        }
      }

      await axios.delete(
        `${API_BASE_URL}/api/car-equipment-types/${typeId}/`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
      );

      // Refresh types
      await fetchTypes();
    } catch (error) {
      console.error('Error deleting equipment type:', error);
      alert('Failed to delete type');
    }
  };

  React.useEffect(() => {
    if (vehicle) {
      setFormData({
        equipment_type: vehicle.equipment_type || 'car',
        car_brand: vehicle.car_brand || '',
        car_model: vehicle.car_model || '',
        plate_number: vehicle.plate_number || '',
        side_number: vehicle.side_number || '',
        number_of_seats: vehicle.number_of_seats || '',
        nfc_tag_number: vehicle.nfc_tag_number || '',
        car_status: vehicle.car_status || 'available',
        technical_condition: vehicle.technical_condition || 'good',
        car_mileage: vehicle.car_mileage || '',
        comment: vehicle.comment || '',
        // Rental fields
        ownership_type: vehicle.ownership_type || 'own',
        rental_start_date: vehicle.rental_start_date || '',
        rental_end_date: vehicle.rental_end_date || '',
        daily_rental_cost: vehicle.daily_rental_cost || ''
      });
    }
  }, [vehicle]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const statusOptions = [
    { value: 'available', label: 'Available' },
    { value: 'service', label: 'Service' },
    { value: 'reservation', label: 'Reservation' },
    { value: 'issued', label: 'Issued' },
    { value: 'damaged', label: 'Damaged' }
  ];

  const conditionOptions = [
    { value: 'excellent', label: 'Excellent' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-steel-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {vehicle ? 'Edit Equipment' : 'Add New Equipment'}
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
            {/* LEFT COLUMN - Equipment Form (2/3 width) */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Equipment Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Equipment Type *
                  </label>
                  <select
                    value={formData.equipment_type}
                    onChange={(e) => handleChange('equipment_type', e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    {localEquipmentTypes.length === 0 ? (
                      <>
                        <option value="car">Car</option>
                        <option value="machine">Machine</option>
                        <option value="lighting_tower">Lighting Tower</option>
                        <option value="cherry_picker">Cherry Picker</option>
                        <option value="lift">Lift</option>
                      </>
                    ) : (
                      localEquipmentTypes.map((type) => (
                        <option key={type.id} value={type.code}>
                          {type.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Brand *
              </label>
              <input
                type="text"
                value={formData.car_brand}
                onChange={(e) => handleChange('car_brand', e.target.value)}
                required
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="e.g., Toyota, Ford"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Model *
              </label>
              <input
                type="text"
                value={formData.car_model}
                onChange={(e) => handleChange('car_model', e.target.value)}
                required
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="e.g., Camry, F-150"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Plate Number *
              </label>
              <input
                type="text"
                value={formData.plate_number}
                onChange={(e) => handleChange('plate_number', e.target.value)}
                required
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="e.g., ABC-123"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Fleet Number
              </label>
              <input
                type="text"
                value={formData.side_number}
                onChange={(e) => handleChange('side_number', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Fleet number"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                NFC Tag Number
              </label>
              <input
                type="text"
                value={formData.nfc_tag_number}
                onChange={(e) => handleChange('nfc_tag_number', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="e.g., A1B2C3D4"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Number of Seats
              </label>
              <input
                type="number"
                value={formData.number_of_seats}
                onChange={(e) => handleChange('number_of_seats', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="5"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Mileage
              </label>
              <input
                type="text"
                value={formData.car_mileage}
                onChange={(e) => handleChange('car_mileage', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="e.g., 50000 km"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Status
              </label>
              <select
                value={formData.car_status}
                onChange={(e) => handleChange('car_status', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Technical Condition
              </label>
              <select
                value={formData.technical_condition}
                onChange={(e) => handleChange('technical_condition', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {conditionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Comments
            </label>
            <textarea
              value={formData.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Additional notes about the vehicle..."
            />
          </div>

          {/* Rental/Ownership Section */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-medium text-slate-900 mb-4">Ownership & Rental Information</h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ownership Type
                </label>
                <select
                  value={formData.ownership_type}
                  onChange={(e) => handleChange('ownership_type', e.target.value)}
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="own">Owned</option>
                  <option value="rented">Rented</option>
                </select>
              </div>

              {formData.ownership_type === 'rented' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Rental Start Date
                      </label>
                      <input
                        type="date"
                        value={formData.rental_start_date}
                        onChange={(e) => handleChange('rental_start_date', e.target.value)}
                        className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Rental End Date
                      </label>
                      <input
                        type="date"
                        value={formData.rental_end_date}
                        onChange={(e) => handleChange('rental_end_date', e.target.value)}
                        className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Daily Rental Cost (PLN)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.daily_rental_cost}
                      onChange={(e) => handleChange('daily_rental_cost', e.target.value)}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                      placeholder="0.00"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 border border-steel-300 rounded-xl hover:bg-steel-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20"
            >
              {vehicle ? 'Update Equipment' : 'Add Equipment'}
            </button>
          </div>
        </form>
      </div>

      {/* RIGHT COLUMN - Equipment Type Management (1/3 width) */}
      <div className="lg:col-span-1 border-l border-steel-200 pl-6">
        <h3 className="text-lg font-semibold mb-4">Equipment Types</h3>

        {/* Add New Type Form */}
        {!showTypeForm ? (
          <button
            type="button"
            onClick={() => setShowTypeForm(true)}
            className="w-full mb-4 px-4 py-2 border-2 border-dashed border-steel-300 rounded-xl text-slate-600 hover:border-brand-500 hover:text-brand-600 transition-colors"
          >
            + Add New Type
          </button>
        ) : (
          <form onSubmit={handleAddType} className="mb-4 p-4 bg-steel-50 rounded-xl space-y-3">
            {typeError && (
              <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                {typeError}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={newTypeName}
                onChange={(e) => {
                  setNewTypeName(e.target.value);
                  setNewTypeCode(e.target.value.toLowerCase().replace(/\s+/g, '_'));
                }}
                placeholder="e.g., Generator"
                required
                className="w-full px-2 py-1.5 text-sm border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Code *
              </label>
              <input
                type="text"
                value={newTypeCode}
                onChange={(e) => setNewTypeCode(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                placeholder="e.g., generator"
                pattern="[a-z_]+"
                title="Lowercase letters and underscores only"
                required
                className="w-full px-2 py-1.5 text-sm border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="text-xs text-slate-500 mt-1">Lowercase & underscores only</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowTypeForm(false);
                  setNewTypeName('');
                  setNewTypeCode('');
                  setTypeError('');
                }}
                className="flex-1 px-3 py-1.5 text-sm border border-steel-300 rounded-xl hover:bg-steel-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20"
              >
                Add
              </button>
            </div>
          </form>
        )}

        {/* Types List */}
        <div className="space-y-2 overflow-y-auto">
          {localEquipmentTypes.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No types yet</p>
          ) : (
            localEquipmentTypes.map((type) => (
              <div
                key={type.id}
                className="flex items-center justify-between p-2 bg-steel-50 rounded-xl hover:bg-steel-100 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">{type.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{type.code}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteType(type.id, type.code)}
                  className="text-red-500 hover:text-red-700 text-sm"
                  title="Delete type"
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>
      </div>

    </div>
        </div>
      </div>
    </div>
  );
};

// Vehicle Details Modal with Tabs
const VehicleDetailsModal = ({ vehicle, user, onClose, onRefreshVehicles }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddComment, setShowAddComment] = useState(false);

  useEffect(() => {
    if (vehicle) {
      fetchVehicleDetails();
    }
  }, [vehicle]);

  const fetchVehicleDetails = async () => {
    try {
      setLoading(true);
      console.log('Fetching details for vehicle ID:', vehicle.id);
      const response = await axios.get(`${API_BASE_URL}/api/cars/${vehicle.id}/detail/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      console.log('Vehicle details response:', response.data);
      console.log('Assignments data:', response.data.assignments);
      console.log('Repairs data:', response.data.repairs);
      console.log('Comments data:', response.data.comments);
      setVehicleData(response.data);
    } catch (error) {
      console.error('Error fetching vehicle details:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'assignments', label: 'Assignments', icon: History },
    { id: 'repairs', label: 'Repairs', icon: Wrench },
    { id: 'comments', label: 'Comments', icon: MessageSquare },
    { id: 'timeline', label: 'Timeline', icon: FileText },
  ];

  if (!vehicle) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 max-w-6xl shadow-lg rounded-2xl bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center">
              <Truck className="mr-3 h-7 w-7 text-brand-600" />
              {vehicle.car_brand} {vehicle.car_model}
            </h2>
            <div className="mt-2 flex items-center space-x-4 text-sm text-slate-600">
              <span className="font-medium">Plate: {vehicle.plate_number}</span>
              {vehicle.side_number && (
                <span>Fleet: {vehicle.side_number}</span>
              )}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                vehicle.car_status === 'available' ? 'bg-green-100 text-green-800' :
                vehicle.car_status === 'issued' ? 'bg-yellow-100 text-yellow-800' :
                vehicle.car_status === 'service' ? 'bg-blue-100 text-blue-800' :
                'bg-red-100 text-red-800'
              }`}>
                {vehicle.car_status}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-steel-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
            </div>
          ) : (
            <div>
              {activeTab === 'overview' && (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Vehicle Information</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Brand & Model</dt>
                        <dd className="text-sm text-slate-900">{vehicle.car_brand} {vehicle.car_model}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Plate Number</dt>
                        <dd className="text-sm text-slate-900">{vehicle.plate_number}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Status</dt>
                        <dd>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            vehicle.car_status === 'available' ? 'bg-green-100 text-green-800' :
                            vehicle.car_status === 'issued' ? 'bg-yellow-100 text-yellow-800' :
                            vehicle.car_status === 'service' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {vehicle.car_status}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Condition</dt>
                        <dd className={`text-sm font-medium ${
                          vehicle.technical_condition === 'excellent' ? 'text-green-600' :
                          vehicle.technical_condition === 'good' ? 'text-brand-600' :
                          vehicle.technical_condition === 'fair' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {vehicle.technical_condition}
                        </dd>
                      </div>
                    </dl>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-4">Statistics</h3>
                    {vehicleData && (
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Total Assignments</dt>
                          <dd className="text-sm text-slate-900">{vehicleData.total_assignments || 0}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Total Repairs</dt>
                          <dd className="text-sm text-slate-900">{vehicleData.total_repairs || 0}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Total Comments</dt>
                          <dd className="text-sm text-slate-900">{vehicleData.total_comments || 0}</dd>
                        </div>
                        {vehicleData.current_assignment && (
                          <div>
                            <dt className="text-sm font-medium text-slate-500">Current Assignment</dt>
                            <dd className="text-sm text-slate-900">
                              {vehicleData.current_assignment.user_name}
                            </dd>
                          </div>
                        )}
                      </dl>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'assignments' && (
                <div>
                  <h3 className="text-lg font-medium mb-4">Assignment History</h3>
                  {console.log('Rendering assignments tab, vehicleData:', vehicleData)}
                  {console.log('Loading state:', loading)}
                  {console.log('Assignments array:', vehicleData?.assignments)}
                  {loading ? (
                    <p className="text-slate-500">Loading assignments...</p>
                  ) : vehicleData && vehicleData.assignments && vehicleData.assignments.length > 0 ? (
                    <div className="space-y-4">
                      {vehicleData.assignments.map((assignment) => (
                        <div key={assignment.id} className="border border-steel-200 rounded-xl p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="font-medium text-slate-900">Assigned to: {assignment.user_name || 'Unknown'}</p>
                                  <p className="text-sm text-slate-600">Project: {assignment.project_name || 'N/A'}</p>
                                  <p className="text-sm text-slate-600">Reason: {assignment.assignment_reason || 'No reason provided'}</p>
                                  <p className="text-sm text-slate-500">Date: {new Date(assignment.assigned_date).toLocaleDateString()}</p>
                                  {assignment.expected_return_date && (
                                    <p className="text-sm text-slate-500">Expected return: {new Date(assignment.expected_return_date).toLocaleDateString()}</p>
                                  )}
                                  {assignment.actual_return_date && (
                                    <p className="text-sm text-slate-500">Returned: {new Date(assignment.actual_return_date).toLocaleDateString()}</p>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-700">Vehicle Details:</p>
                                  <p className="text-sm text-slate-600">Plate: {vehicle.plate_number}</p>
                                  <p className="text-sm text-slate-600">Brand: {vehicle.car_brand} {vehicle.car_model}</p>
                                  {assignment.mileage_start && (
                                    <p className="text-sm text-slate-500">Start mileage: {assignment.mileage_start} km</p>
                                  )}
                                  {assignment.mileage_end && (
                                    <p className="text-sm text-slate-500">End mileage: {assignment.mileage_end} km</p>
                                  )}
                                  {assignment.mileage_used && (
                                    <p className="text-sm text-green-600 font-medium">Distance used: {assignment.mileage_used} km</p>
                                  )}
                                </div>
                              </div>
                              {assignment.assignment_notes && (
                                <div className="mt-3 p-2 bg-blue-50 rounded">
                                  <p className="text-xs text-slate-500 font-medium">Notes:</p>
                                  <p className="text-sm text-slate-700">{assignment.assignment_notes}</p>
                                </div>
                              )}
                              {assignment.return_notes && (
                                <div className="mt-2 p-2 bg-steel-50 rounded">
                                  <p className="text-xs text-slate-500 font-medium">Return Notes:</p>
                                  <p className="text-sm text-slate-700">{assignment.return_notes}</p>
                                </div>
                              )}
                              {assignment.assigned_items && assignment.assigned_items.length > 0 && (
                                <div className="mt-3 p-3 bg-green-50 rounded border-l-4 border-green-400">
                                  <div className="flex justify-between items-center mb-2">
                                    <p className="text-sm font-medium text-green-800">Items loaded on vehicle</p>
                                    {assignment.assigned_items[0]?.issue_number && (
                                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        Issue #{assignment.assigned_items[0].issue_number}
                                      </span>
                                    )}
                                  </div>
                                  <div className="space-y-2">
                                    {assignment.assigned_items.map((item, index) => (
                                      <div key={index} className="bg-white p-2 rounded border">
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1">
                                            <p className="font-medium text-slate-900">{item.item_name}</p>
                                            {item.category && (
                                              <p className="text-xs text-slate-500">Category: {item.category}</p>
                                            )}
                                            {item.project_name && (
                                              <p className="text-xs text-brand-600">Project: {item.project_name}</p>
                                            )}
                                          </div>
                                          <div className="text-right">
                                            <p className="text-sm font-medium text-slate-700">
                                              {item.quantity} {item.unit}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="mt-2 text-xs text-slate-600">
                                    Total items: {assignment.assigned_items.length}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="ml-4 text-right">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                assignment.status === 'assigned' ? 'bg-green-100 text-green-800' : 
                                assignment.status === 'returned' ? 'bg-blue-100 text-blue-800' :
                                'bg-steel-100 text-slate-800'
                              }`}>
                                {assignment.status}
                              </span>
                              {assignment.duration_days && (
                                <p className="text-xs text-slate-500 mt-1">{assignment.duration_days} days</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">No assignments found for this vehicle.</p>
                  )}
                </div>
              )}

              {activeTab === 'repairs' && (
                <div>
                  <h3 className="text-lg font-medium mb-4">Repair History</h3>
                  {loading ? (
                    <p className="text-slate-500">Loading repairs...</p>
                  ) : vehicleData && vehicleData.repairs && vehicleData.repairs.length > 0 ? (
                    <div className="space-y-4">
                      {vehicleData.repairs.map((repair) => (
                        <div key={repair.id} className="border border-steel-200 rounded-xl p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium">{repair.repair_type}</p>
                              <p className="text-sm text-slate-600">{repair.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                                <span>Scheduled: {repair.scheduled_date ? new Date(repair.scheduled_date).toLocaleDateString() : 'Not scheduled'}</span>
                                {repair.estimated_cost && <span>Est. Cost: €{repair.estimated_cost}</span>}
                                {repair.service_provider && <span>Provider: {repair.service_provider}</span>}
                              </div>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              repair.status === 'completed' ? 'bg-green-100 text-green-800' : 
                              repair.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' : 
                              'bg-steel-100 text-slate-800'
                            }`}>
                              {repair.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">No repairs found for this vehicle.</p>
                  )}
                </div>
              )}

              {activeTab === 'comments' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium">Comments & Notes</h3>
                    <button
                      onClick={() => setShowAddComment(true)}
                      className="px-3 py-2 bg-green-600 text-white text-sm rounded-xl hover:bg-green-700 transition-colors flex items-center"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Add Comment
                    </button>
                  </div>
                  {loading ? (
                    <p className="text-slate-500">Loading comments...</p>
                  ) : vehicleData && vehicleData.comments && vehicleData.comments.length > 0 ? (
                    <div className="space-y-4">
                      {vehicleData.comments.map((comment) => (
                        <div key={comment.id} className={`border border-steel-200 rounded-xl p-4 ${
                          comment.is_resolved ? 'bg-green-50 border-green-200' : 'bg-steel-50'
                        }`}>
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h4 className="font-medium text-slate-900">
                                  {comment.title || `${comment.comment_type} Comment`}
                                </h4>
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  comment.comment_type === 'issue' ? 'bg-red-100 text-red-800' :
                                  comment.comment_type === 'maintenance' ? 'bg-blue-100 text-blue-800' :
                                  comment.comment_type === 'damage' ? 'bg-orange-100 text-orange-800' :
                                  'bg-steel-100 text-slate-800'
                                }`}>
                                  {comment.comment_type}
                                </span>
                                {comment.is_urgent && !comment.is_resolved && (
                                  <span className="px-2 py-1 text-xs bg-red-500 text-white rounded-full font-medium">
                                    URGENT
                                  </span>
                                )}
                                {comment.is_resolved && (
                                  <span className="px-2 py-1 text-xs bg-green-500 text-white rounded-full font-medium flex items-center">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    RESOLVED
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-700 mb-3">{comment.content}</p>
                              <div className="grid grid-cols-2 gap-4 text-xs text-slate-500">
                                <div>
                                  <span className="font-medium">Added by:</span> {comment.created_by_name || comment.user_name || 'Unknown'}
                                </div>
                                <div>
                                  <span className="font-medium">Date:</span> {new Date(comment.created_date || comment.created_at).toLocaleString()}
                                </div>
                                {comment.mileage_at_comment && (
                                  <div>
                                    <span className="font-medium">Mileage:</span> {comment.mileage_at_comment} km
                                  </div>
                                )}
                                {comment.location && (
                                  <div>
                                    <span className="font-medium">Location:</span> {comment.location}
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* Action buttons */}
                            <div className="ml-4 flex flex-col space-y-2">
                              {comment.is_urgent && !comment.is_resolved && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.patch(`${API_BASE_URL}/api/vehicle-comments/${comment.id}/`, {
                                        is_resolved: true,
                                        resolved_date: new Date().toISOString(),
                                        resolved_by: user?.id
                                      }, {
                                        headers: {
                                          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                                          'Content-Type': 'application/json'
                                        }
                                      });
                                      
                                      // Refresh vehicle details and main list
                                      await fetchVehicleDetails();
                                      if (onRefreshVehicles) {
                                        onRefreshVehicles();
                                      }
                                      alert('Issue marked as resolved!');
                                    } catch (error) {
                                      console.error('Error resolving issue:', error);
                                      alert('Error resolving issue. Please try again.');
                                    }
                                  }}
                                  className="px-3 py-1 bg-green-600 text-white text-xs rounded-xl hover:bg-green-700 transition-colors flex items-center"
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Mark as Resolved
                                </button>
                              )}
                              {comment.is_resolved && (
                                <div className="text-xs text-green-600">
                                  <p>✓ Resolved</p>
                                  {comment.resolved_date && (
                                    <p>{new Date(comment.resolved_date).toLocaleDateString()}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-steel-50 rounded-xl">
                      <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No comments found for this vehicle.</p>
                      <p className="text-sm text-slate-500 mt-1">Comments and notes will appear here.</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'timeline' && (
                <div>
                  <h3 className="text-lg font-medium mb-4">Activity Timeline</h3>
                  {vehicleData && vehicleData.recent_history && (
                    <div className="space-y-4">
                      {vehicleData.recent_history.map((event, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <div className="w-2 h-2 bg-brand-600 rounded-full"></div>
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-900">{event.title}</p>
                            {event.description && (
                              <p className="text-sm text-slate-500">{event.description}</p>
                            )}
                            <p className="text-xs text-slate-400">
                              {new Date(event.created_date).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Comment Modal */}
        {showAddComment && (
          <AddCommentModal
            vehicle={vehicle}
            user={user}
            onClose={() => setShowAddComment(false)}
            onSave={async (commentData) => {
              try {
                console.log('Saving comment from details modal:', commentData);
                
                // Save comment
                await axios.post(`${API_BASE_URL}/api/vehicle-comments/`, commentData, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json'
                  }
                });

                // Update vehicle mileage if provided
                if (commentData.mileage_at_comment && commentData.mileage_at_comment !== vehicle.car_mileage) {
                  await axios.patch(`${API_BASE_URL}/api/cars/${vehicle.id}/`, {
                    car_mileage: commentData.mileage_at_comment
                  }, {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                      'Content-Type': 'application/json'
                    }
                  });
                }

                setShowAddComment(false);
                // Refresh vehicle details
                await fetchVehicleDetails();
                // Refresh main vehicle list
                if (onRefreshVehicles) {
                  onRefreshVehicles();
                }
                alert('Comment added successfully!');
              } catch (error) {
                console.error('Error saving comment:', error);
                alert('Error saving comment. Please try again.');
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

// Quick Repair Modal
const QuickRepairModal = ({ vehicle, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    repair_type: 'maintenance',
    title: '',
    description: '',
    priority: 'medium',
    scheduled_date: '',
    estimated_cost: '',
    service_provider: '',
    mileage_at_repair: vehicle?.car_mileage || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const repairData = {
      ...formData,
      car: vehicle.id,
      scheduled_date: formData.scheduled_date || null,
      status: 'scheduled',
      service_provider: formData.service_provider,
      estimated_cost: formData.estimated_cost || null
    };
    onSave(repairData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!vehicle) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          Quick Repair - {vehicle.car_brand} {vehicle.car_model}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Repair Type *
              </label>
              <select
                value={formData.repair_type}
                onChange={(e) => handleChange('repair_type', e.target.value)}
                required
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="maintenance">Routine Maintenance</option>
                <option value="repair">Repair</option>
                <option value="inspection">Inspection</option>
                <option value="accident">Accident Repair</option>
                <option value="upgrade">Upgrade/Modification</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Brief description of repair needed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              required
              rows={3}
              className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Detailed description of work needed"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Scheduled Date
              </label>
              <input
                type="datetime-local"
                value={formData.scheduled_date}
                onChange={(e) => handleChange('scheduled_date', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Estimated Cost
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.estimated_cost}
                onChange={(e) => handleChange('estimated_cost', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Service Provider
              </label>
              <input
                type="text"
                value={formData.service_provider}
                onChange={(e) => handleChange('service_provider', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Workshop or mechanic"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Current Mileage
              </label>
              <input
                type="number"
                value={formData.mileage_at_repair}
                onChange={(e) => handleChange('mileage_at_repair', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Current mileage"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 border border-steel-300 rounded-xl hover:bg-steel-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/20"
            >
              Schedule Repair
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Comment Modal
const AddCommentModal = ({ vehicle, user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    comment_type: 'general',
    title: '',
    content: '',
    is_urgent: false,
    mileage_at_comment: vehicle?.car_mileage || '',
    location: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('User from props:', user);
    onSave({ 
      ...formData, 
      car: vehicle.id,
      user: user?.id || user?.user_id, // Add user ID from props
      created_by: user?.id || user?.user_id // Alternative field name
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!vehicle) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          Add Comment - {vehicle.car_brand} {vehicle.car_model}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Comment Type
              </label>
              <select
                value={formData.comment_type}
                onChange={(e) => handleChange('comment_type', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="general">General Note</option>
                <option value="issue">Issue/Problem</option>
                <option value="maintenance">Maintenance Note</option>
                <option value="inspection">Inspection Note</option>
                <option value="damage">Damage Report</option>
                <option value="fuel">Fuel/Cost Note</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_urgent}
                  onChange={(e) => handleChange('is_urgent', e.target.checked)}
                  className="rounded border-steel-300 text-red-600 focus:ring-red-500"
                />
                <span className="ml-2 text-sm font-medium text-red-600">Mark as Urgent</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Brief title for the comment"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Comment *
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => handleChange('content', e.target.value)}
              required
              rows={4}
              className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Enter your comment or observation..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Current Mileage
              </label>
              <input
                type="number"
                value={formData.mileage_at_comment}
                onChange={(e) => handleChange('mileage_at_comment', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Current mileage"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Where was this observed?"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 border border-steel-300 rounded-xl hover:bg-steel-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
            >
              Add Comment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleManagement;